import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
  DataGrid,
  type GridRowIdGetter,
  type GridRowParams,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import {
  formatCivilDate,
  formatInstant,
  isCivilDate,
  parseOrder,
  renderOrder,
  resolveFormattingLocale,
  type InfiniteList,
} from '@eis/core';
import { dataGridDefaults, dataGridSx } from '@eis/theme';
import { EmptyState } from '../feedback/EmptyState';
import type { ExportableGridColDef } from '../jobs/useGridExportColumns';
import { ErrorState } from '../feedback/ErrorState';

/**
 * The ONE list grid of the console (SDD-017 §9, L-12): the community Data Grid over the pages of
 * `useInfiniteList`, with no page controls anywhere.
 *
 * Paging is the scroll. The scroll-end signal is an IntersectionObserver on a sentinel in
 * `slots.footer`: the community build of `@mui/x-data-grid` 9.12 has no `onRowsScrollEnd` prop
 * (that is Pro), and its `rowsScrollEndIntersection` event is declared in the event lookup only,
 * `@ignore`d and never published by the community code. The grid therefore grows with its rows
 * (`autoHeight`) and the PAGE (or the nearest scrolling ancestor) scrolls; the sentinel sits at the
 * grid's end and fetches the next page when it comes within `prefetchViewports` of the visible
 * area. Rows are not virtualised in this mode — a scroll of a few hundred rows is what an operator
 * reads; more is a filter.
 *
 * `paginationMode="server"` is what lets the MIT grid show more than 100 rows: in server mode it
 * does not slice the rows it is given. Sorting is server-side and maps to `order`
 * (`-validFrom` ↔ validFrom descending); filtering is the property filter's, so the column filter
 * menu is off.
 *
 * For keyboard and screen-reader users the footer always offers a visible "Mehr laden" button —
 * the scroll is a convenience, never the only way. The footer also states what is loaded
 * ("50 geladen", "≈ 1 200" with a total) and the end of the list; the first load is a skeleton,
 * an empty result the EmptyState, a refused or failed page 1 the ErrorState with retry, a failed
 * later page a retry at the bottom with the rows kept.
 *
 * Temporal lists (`showCut`) carry the cut in a header — "Stand: 03.10.2026 · erfasst 11:14" —
 * with a refresh that starts a new cut (§6).
 */

/** The slice of {@link InfiniteList} the grid reads. */
export type InfiniteGridList = Pick<
  InfiniteList<unknown>,
  | 'isLoading'
  | 'isFetchingNextPage'
  | 'hasNextPage'
  | 'fetchNextPage'
  | 'isError'
  | 'isNextPageError'
  | 'retry'
  | 'refresh'
  | 'cut'
  | 'totalSize'
  | 'totalSizeCapped'
  | 'listError'
  | 'error'
>;

export interface InfiniteDataGridProps<Row extends GridValidRowModel> {
  /** The rows of every loaded page, in server order (mapped from `list.items`). */
  readonly rows: readonly Row[];
  /** The list state from `useInfiniteList`. */
  readonly list: InfiniteGridList;
  /** The columns; `exportPath` / `exportType` make one exportable (SDD-020 §9). */
  readonly columns: readonly ExportableGridColDef<Row>[];
  /** The grid's accessible name. */
  readonly ariaLabel: string;
  readonly getRowId?: GridRowIdGetter<Row>;
  /** The URL's `order` (`-validFrom`). */
  readonly order?: string;
  readonly onOrderChange?: (order: string) => void;
  /**
   * Column field → catalog key, for every column the server can sort by (the catalog's
   * `sortable` fields). Any column not named here is not sortable.
   */
  readonly sortKeys?: Readonly<Record<string, string>>;
  /** Temporal lists: the "Stand … · erfasst …" header with refresh. */
  readonly showCut?: boolean;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  readonly emptyAction?: ReactNode;
  /**
   * The error state's sentence when page 1 failed for a reason that is not a list refusal — the
   * screen's own words ("the billing service is unavailable"). A refusal (`INVALID_FILTER` …)
   * always says what was refused.
   */
  readonly errorDescription?: string;
  readonly onRowClick?: (row: Row) => void;
  /** How many visible heights before the end the next page is fetched. Default 2 (§9). */
  readonly prefetchViewports?: number;
}

interface FooterState {
  readonly list: InfiniteGridList;
  readonly loaded: number;
  readonly prefetchViewports: number;
}

const FooterContext = createContext<FooterState | null>(null);

/** The nearest ancestor that scrolls vertically, or `null` for the viewport. */
function scrollParentOf(element: HTMLElement | null): HTMLElement | null {
  let current = element?.parentElement ?? null;
  while (current !== null && current !== document.body) {
    const overflow = getComputedStyle(current).overflowY;
    if (overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') return current;
    current = current.parentElement;
  }
  return null;
}

function useLocale(): string {
  const { i18n } = useTranslation('patterns');
  return resolveFormattingLocale(i18n.language);
}

/**
 * `slots.footer`: the sentinel, the bottom progress, a later page's retry, the visible
 * "Mehr laden", the end of the list, and what is loaded.
 */
function InfiniteFooter(): ReactNode {
  const state = useContext(FooterContext);
  const { t } = useTranslation('patterns');
  const locale = useLocale();
  const sentinel = useRef<HTMLDivElement | null>(null);
  const list = state?.list;
  const hasNextPage = list?.hasNextPage === true;
  const fetching = list?.isFetchingNextPage === true;
  const failed = list?.isNextPageError === true;
  const fetchNextPage = list?.fetchNextPage;
  const loaded = state?.loaded ?? 0;
  const prefetch = state?.prefetchViewports ?? 2;

  // A fresh observer per page: its first callback reports where the sentinel is NOW, so a page
  // that still leaves the end in reach fetches the next one, and one that pushed it away does not.
  useEffect(() => {
    const target = sentinel.current;
    if (target === null || typeof IntersectionObserver === 'undefined') return undefined;
    if (!hasNextPage || fetching || failed || fetchNextPage === undefined) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
      },
      { root: scrollParentOf(target), rootMargin: `0px 0px ${String(prefetch * 100)}% 0px` },
    );
    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, fetching, failed, fetchNextPage, loaded, prefetch]);

  if (list === undefined) return null;
  const number = new Intl.NumberFormat(locale);
  const total = list.totalSize;

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
      <Box ref={sentinel} aria-hidden sx={{ height: '1px' }} />
      <Box sx={{ height: 4 }}>
        {fetching && <LinearProgress aria-label={t('infiniteGrid.loadingMore')} />}
      </Box>
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          px: 1.5,
          py: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary" role="status" aria-live="polite">
          {t('infiniteGrid.loaded', { formatted: number.format(loaded) })}
          {total !== undefined &&
            ` · ${
              list.totalSizeCapped
                ? t('infiniteGrid.totalCapped', { formatted: number.format(total) })
                : t('infiniteGrid.totalApprox', { formatted: number.format(total) })
            }`}
        </Typography>
        {failed ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" color="error">
              {t('infiniteGrid.nextPageFailed')}
            </Typography>
            <Button size="small" variant="outlined" onClick={list.retry}>
              {t('actions.retry')}
            </Button>
          </Stack>
        ) : hasNextPage ? (
          <Button size="small" variant="outlined" onClick={list.fetchNextPage} disabled={fetching}>
            {t('infiniteGrid.loadMore')}
          </Button>
        ) : (
          loaded > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('infiniteGrid.endOfList')}
            </Typography>
          )
        )}
      </Stack>
    </Box>
  );
}

/** "Stand: 03.10.2026 · erfasst 11:14" and the refresh that starts a new cut. */
function CutHeader({ list }: { readonly list: InfiniteGridList }): ReactNode {
  const { t } = useTranslation('patterns');
  const locale = useLocale();
  const cut = list.cut;
  if (cut === undefined) return null;
  const validAt =
    cut.validAt !== undefined && isCivilDate(cut.validAt)
      ? formatCivilDate(cut.validAt, locale)
      : undefined;
  const asOf = cut.asOf === undefined ? undefined : formatInstant(cut.asOf, locale);
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', justifyContent: 'flex-end', minHeight: 32 }}
    >
      <Typography variant="caption" color="text.secondary">
        {[
          validAt === undefined ? undefined : t('infiniteGrid.validAt', { date: validAt }),
          asOf === undefined ? undefined : t('infiniteGrid.asOf', { time: asOf }),
        ]
          .filter((part) => part !== undefined)
          .join(' · ')}
      </Typography>
      <Button
        size="small"
        variant="text"
        startIcon={<RefreshOutlinedIcon fontSize="small" />}
        onClick={list.refresh}
        aria-label={t('infiniteGrid.refreshLabel')}
      >
        {t('infiniteGrid.refresh')}
      </Button>
    </Stack>
  );
}

const SINGLE_PAGE = { page: 0, pageSize: 100 } as const;

const ERROR_CODES = [
  'INVALID_FILTER',
  'INVALID_SEARCH',
  'INVALID_ORDER',
  'INVALID_PAGE_TOKEN',
  'INVALID_EXPAND',
  'INVALID_ARGUMENT',
] as const;

export function InfiniteDataGrid<Row extends GridValidRowModel>({
  rows,
  list,
  columns,
  ariaLabel,
  getRowId,
  order,
  onOrderChange,
  sortKeys,
  showCut = false,
  emptyTitle,
  emptyDescription,
  emptyAction,
  errorDescription,
  onRowClick,
  prefetchViewports = 2,
}: InfiniteDataGridProps<Row>): ReactNode {
  const { t } = useTranslation('patterns');

  const keyToField = useMemo(
    () => new Map(Object.entries(sortKeys ?? {}).map(([field, key]) => [key, field])),
    [sortKeys],
  );
  const sortModel: GridSortModel = useMemo(
    () =>
      parseOrder(order).flatMap((item) => {
        const field = keyToField.get(item.field);
        return field === undefined ? [] : [{ field, sort: item.sort ?? null }];
      }),
    [order, keyToField],
  );
  const sortableColumns = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        sortable: sortKeys?.[column.field] !== undefined,
      })),
    [columns, sortKeys],
  );
  const footer = useMemo<FooterState>(
    () => ({ list, loaded: rows.length, prefetchViewports }),
    [list, rows.length, prefetchViewports],
  );

  const header = showCut ? <CutHeader list={list} /> : null;

  if (list.isError) {
    const code = list.listError?.code;
    const known =
      code !== undefined && (ERROR_CODES as readonly string[]).includes(code) ? code : undefined;
    return (
      <Stack spacing={1}>
        {header}
        <ErrorState
          title={t('infiniteGrid.error.title')}
          description={
            known === undefined
              ? errorDescription
              : t(`infiniteGrid.error.${known}`, { field: list.listError?.field ?? '' })
          }
          details={
            list.listError?.message ??
            (list.error instanceof Error ? list.error.message : undefined)
          }
          onRetry={list.retry}
        />
      </Stack>
    );
  }

  if (!list.isLoading && rows.length === 0) {
    return (
      <Stack spacing={1}>
        {header}
        <EmptyState
          title={emptyTitle ?? t('infiniteGrid.empty.title')}
          description={emptyDescription ?? t('infiniteGrid.empty.description')}
          action={emptyAction}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      {header}
      <FooterContext.Provider value={footer}>
        <DataGrid<Row>
          {...dataGridDefaults}
          sx={{
            ...dataGridSx,
            ...(onRowClick === undefined ? {} : { '& .MuiDataGrid-row': { cursor: 'pointer' } }),
          }}
          autoHeight
          rows={rows}
          columns={sortableColumns}
          {...(getRowId === undefined ? {} : { getRowId })}
          aria-label={ariaLabel}
          loading={list.isLoading}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          slots={{ footer: InfiniteFooter }}
          // Server-side everything: the grid shows the pages it is given and slices nothing.
          paginationMode="server"
          rowCount={rows.length}
          // The MIT grid refuses a page size above 100 — and in server mode it slices nothing, so
          // one fixed "page" shows every loaded row.
          paginationModel={SINGLE_PAGE}
          sortingMode="server"
          filterMode="server"
          disableColumnFilter
          sortingOrder={['asc', 'desc', null]}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            onOrderChange?.(
              renderOrder(
                model.flatMap((item) => {
                  const key = sortKeys?.[item.field];
                  return key === undefined ? [] : [{ field: key, sort: item.sort }];
                }),
              ),
            );
          }}
          {...(onRowClick === undefined
            ? {}
            : {
                onRowClick: (params: GridRowParams<Row>) => {
                  onRowClick(params.row);
                },
              })}
        />
      </FooterContext.Provider>
    </Stack>
  );
}
